<template>
  <v-navigation-drawer
    v-model="drawer"
    app
    right
    temporary
    width="600"
    class="legal-assistant-drawer"
  >
    <v-toolbar color="#eeeff0" flat elevation="0" style="border-radius: 0; border-bottom: 1px solid rgba(43, 43, 65, 0.08);">
      <v-toolbar-title style="font-size: 1.6rem; font-weight: 600; color: #3325ae;">
        AI asistent za hrvatske zakone
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn v-if="conversationHistory.length > 0" icon @click="clearConversation" title="Nova konverzacija">
        <v-icon color="#2b2b41">mdi-refresh</v-icon>
      </v-btn>
      <v-btn icon @click="drawer = false">
        <v-icon color="#2b2b41">mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-container fluid class="pa-4 d-flex flex-column" style="height: calc(100vh - 64px); background-color: #eeeff0;">

      <!-- CONVERSATION HISTORY -->
      <div ref="conversationContainer" class="conversation-container flex-grow-1 overflow-y-auto mb-4">
        <div v-if="conversationHistory.length === 0" class="empty-state pa-8">
          <h3 class="empty-state-title mb-5">
            AI asistent za brzo pretraživanje<br>i analizu hrvatskih zakona
          </h3>

          <div class="example-questions">
            <div class="example-label mb-3">Primjeri pitanja:</div>
            <v-chip
              v-for="(example, idx) in exampleQuestions"
              :key="idx"
              class="example-chip ma-2"
              outlined
              @click="question = example.fullQuestion"
            >
              <v-icon left :color="example.color" size="18">{{ example.icon }}</v-icon>
              {{ example.text }}
            </v-chip>
          </div>
        </div>

        <div
          v-for="(message, index) in conversationHistory"
          :key="index"
          :class="['message', 'mb-3', message.isUser ? 'user-message' : 'assistant-message']"
        >
          <div v-if="message.isUser" class="message-content pa-3">
            <div>{{ message.text }}</div>
          </div>
          <div v-else class="message-content pa-3">
            <div class="answer-title mb-4" v-if="message.title">
              {{ message.title }}
            </div>
            <div v-html="message.text" class="legal-response-content"></div>

            <!-- Citations -->
            <div v-if="message.citations && message.citations.length > 0" class="citations mt-6">
              <div class="citations-header mb-4">
                <v-icon size="20" color="#3325ae">mdi-file-document-multiple</v-icon>
                <span class="citations-header-text">
                  Pravni izvori ({{ message.citations.length }})
                </span>
              </div>

              <div class="citations-grid">
                <div
                  v-for="(citation, idx) in message.citations"
                  :key="idx"
                  class="citation-card"
                  @click="openCitation(citation)"
                >
                  <div class="citation-card-header">
                    <v-icon size="18" color="#3325ae">mdi-gavel</v-icon>
                    <span class="citation-law-name">{{ citation.law_name }}</span>
                  </div>
                  <div class="citation-article">Broj članka: {{ citation.article_number }}</div>
                  <div class="citation-similarity">
                    <v-progress-linear
                      :value="citation.similarity * 100"
                      height="5"
                      color="#0acab1"
                      background-color="#e8e8f0"
                      rounded
                    ></v-progress-linear>
                    <span class="citation-similarity-text">
                      {{ (citation.similarity * 100).toFixed(0) }}% podudaranje
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading indicator -->
        <div v-if="loading" class="message assistant-message mb-3">
          <div class="message-content loading-message pa-4">
            <div class="loading-text">
              <div class="loading-title">Pretražujem zakone...</div>
              <div class="loading-steps">
                <div class="loading-step">
                  <v-icon size="18" color="#0acab1">mdi-check-circle</v-icon>
                  <span>Analiziram upit</span>
                </div>
                <div class="loading-step active">
                  <v-icon size="18" color="#3325ae">mdi-loading mdi-spin</v-icon>
                  <span>Pretraživanje baze zakona</span>
                </div>
                <div class="loading-step">
                  <v-icon size="18" color="#e8e8f0">mdi-circle-outline</v-icon>
                  <span>Generiranje odgovora</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div :class="['flex-shrink-0', 'input-section', conversationHistory.length > 0 ? 'compact' : '']">
        <!-- Add helpful tip above input -->
        <div v-if="conversationHistory.length === 0" class="input-tip mb-3">
          <v-icon size="16" color="#0acab1">mdi-lightbulb-on-outline</v-icon>
          <span>Postavite konkretno pravno pitanje za najbolje rezultate</span>
        </div>

        <v-textarea
          v-model="question"
          outlined
          :label="conversationHistory.length > 0 ? 'Nastavite pravni razgovor...' : 'Postavite pravno pitanje...'"
          :placeholder="conversationHistory.length > 0 ? 'npr. Možete li mi detaljnije objasniti...' : 'npr. Koliko iznosi minimalni godišnji odmor prema Zakonu o radu?'"
          :rows="conversationHistory.length > 0 ? 2 : 4"
          :disabled="loading"
          @keydown.ctrl.enter="submitQuery"
          hide-details
          class="legal-input"
        ></v-textarea>

        <v-btn
          block
          large
          :loading="loading"
          :disabled="!question || loading"
          @click="submitQuery"
          elevation="0"
          :class="['submit-btn', conversationHistory.length > 0 ? 'mt-3' : 'mt-6', conversationHistory.length > 0 ? 'compact' : '']"
        >
          <v-icon left size="20" class="send-icon">mdi-send</v-icon>
          Pošalji pitanje
        </v-btn>
      </div>
    </v-container>

    <!-- Citation Dialog -->
    <v-dialog v-model="citationDialog" max-width="800" content-class="citation-dialog">
      <v-card v-if="selectedCitation" class="citation-detail-card" elevation="8">
        <!-- Header with legal document styling -->
        <div class="citation-header">
          <div class="citation-header-pattern"></div>
          <div class="citation-header-content">
            <v-icon size="32" color="white">mdi-gavel</v-icon>
            <div class="citation-header-text">
              <div class="citation-law-title">{{ selectedCitation.law_name }}</div>
              <div class="citation-article-title">Broj članka: {{ selectedCitation.article_number }}</div>
            </div>
          </div>
          <v-btn
            icon
            dark
            @click="citationDialog = false"
            class="citation-close-btn"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Content with document styling -->
        <v-card-text class="citation-content pa-6">
          <div class="citation-text">
            <div class="citation-label mb-3">
              <v-icon size="18" color="#3325ae">mdi-text</v-icon>
              <span>Tekst odredbe</span>
            </div>
            <p class="text-body-1 citation-body">{{ selectedCitation.content }}</p>
          </div>

          <v-divider class="my-5" style="border-color: #e8e8f0;"></v-divider>

          <!-- Metadata section -->
          <div class="citation-metadata">
            <div class="metadata-item">
              <v-icon size="18" color="#0acab1">mdi-chart-arc</v-icon>
              <span class="metadata-label">Podudaranje:</span>
              <v-chip small color="#e1fefa" text-color="#0acab1" class="font-weight-bold">
                {{ (selectedCitation.similarity * 100).toFixed(0) }}%
              </v-chip>
            </div>
          </div>
        </v-card-text>

        <!-- Actions -->
        <v-card-actions class="citation-actions pa-4">
          <v-btn
            @click="copyCitation"
            text
            rounded
            elevation="0"
            class="font-weight-600 copy-citation-btn"
          >
            <v-icon left color="#2b2b41">mdi-content-copy</v-icon>
            Kopiraj citat
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-navigation-drawer>
</template>

<script>
import legalRag from '@/apiCallers/promise_handler_db/legalRag';

const STORAGE_KEY = 'legal-rag-conversation';
const STORAGE_VERSION = '1.0';
const CLEARED_FLAG_KEY = 'legal-rag-cleared';

export default {
  name: 'LegalAssistantDrawerLite',

  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      question: '',
      loading: false,
      conversationHistory: [],
      previousCitations: [],
      citationDialog: false,
      selectedCitation: null,

      // Streaming batching for smooth updates
      streamBuffer: '',
      streamUpdateTimer: null,

      exampleQuestions: [
        {
          text: 'Zastara potraživanja po Zakonu o obveznim odnosima',
          icon: 'mdi-clock-alert',
          color: '#3325AE',
          fullQuestion: 'Koliki je rok zastare za potraživanja iz ugovora o djelu prema Zakonu o obveznim odnosima? Kada počinje teći rok zastare i postoje li razlike za različite vrste ugovornih odnosa?'
        },
        {
          text: 'Naknada štete kod odgovornosti poslodavca',
          icon: 'mdi-gavel',
          color: '#e31639',
          fullQuestion: 'Pod kojim uvjetima poslodavac odgovara za štetu prema Zakonu o radu? Kako se utvrđuje visina naknade štete i koji su rokovi za podnošenje zahtjeva za naknadu štete?'
        },
        {
          text: 'Stjecanje prava vlasništva dosjelošću',
          icon: 'mdi-home-city',
          color: '#3325AE',
          fullQuestion: 'Koji su uvjeti za stjecanje prava vlasništva dosjelošću na nekretnini prema Zakonu o vlasništvu? Koliko iznosi rok dosjelosti za posjed u dobroj i zloj vjeri?'
        },
        {
          text: 'Otkaz ugovora o radu iz poslovnih razloga',
          icon: 'mdi-briefcase-remove',
          color: '#e31639',
          fullQuestion: 'Koje su pretpostavke za valjan otkaz ugovora o radu iz poslovnih razloga prema Zakonu o radu? Koja je procedura otpuštanja i koja su prava radnika u takvom slučaju?'
        }
      ]
    };
  },

  computed: {
    drawer: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    }
  },

  watch: {
    drawer(isOpen) {
      if (isOpen) {
        this.loadConversationFromStorage();
      }
    }
  },

  mounted() {
    this.loadConversationFromStorage();
  },

  methods: {
    // Sanitize text to remove Cyrillic and other non-Croatian characters
    sanitizeText(text) {
      // Map common Cyrillic lookalikes to Latin equivalents
      const cyrillicToLatin = {
        'а': 'a', 'е': 'e', 'о': 'o', 'р': 'p', 'с': 'c', 'у': 'u', 'х': 'x',
        'А': 'A', 'В': 'B', 'Е': 'E', 'К': 'K', 'М': 'M', 'Н': 'H', 'О': 'O',
        'Р': 'P', 'С': 'C', 'Т': 'T', 'У': 'U', 'Х': 'X',
        'в': 'v', 'н': 'n', 'к': 'k', 'м': 'm', 'т': 't'
      };

      let sanitized = text;

      // Replace Cyrillic with Latin equivalents
      for (const [cyrillic, latin] of Object.entries(cyrillicToLatin)) {
        sanitized = sanitized.replace(new RegExp(cyrillic, 'g'), latin);
      }

      // Remove any remaining Cyrillic characters (U+0400 to U+04FF)
      sanitized = sanitized.replace(/[\u0400-\u04FF]/g, '');

      return sanitized;
    },

    // Batched streaming for smooth updates
    addToStreamBuffer(text, assistantMessage) {
      this.streamBuffer += text;

      // Clear existing timer
      if (this.streamUpdateTimer) {
        clearTimeout(this.streamUpdateTimer);
      }

      // Batch updates every 100ms for smoother appearance
      this.streamUpdateTimer = setTimeout(() => {
        if (this.streamBuffer) {
          let cleanText = this.streamBuffer;

          // Aggressively filter out all marker-related content
          // Remove everything before ===HTML=== (including the marker)
          if (cleanText.includes('===HTML===')) {
            cleanText = cleanText.split('===HTML===').slice(1).join('===HTML===');
          }

          // If buffer still contains ===TITLE=== but no ===HTML===, don't display yet
          if (cleanText.includes('===TITLE===') && !cleanText.includes('===HTML===')) {
            // Wait for more content
            return;
          }

          // Remove all remaining markers and any text on same line as markers
          cleanText = cleanText.split('\n').filter(line => {
            // Skip lines that contain === markers
            return !line.includes('===');
          }).join('\n');

          // Sanitize to remove Cyrillic and other non-Croatian characters
          cleanText = this.sanitizeText(cleanText);

          // Only add if we have actual content
          if (cleanText.trim()) {
            assistantMessage.text += cleanText;
            this.$forceUpdate();

            // Smart scroll after batched update
            this.$nextTick(() => {
              this.smartScrollToBottom();
            });
          }

          this.streamBuffer = '';
        }
      }, 100);
    },

    flushStreamBuffer(assistantMessage) {
      // Immediately flush any remaining buffered text
      if (this.streamUpdateTimer) {
        clearTimeout(this.streamUpdateTimer);
        this.streamUpdateTimer = null;
      }

      if (this.streamBuffer) {
        let cleanText = this.streamBuffer;

        // Remove everything before ===HTML===
        if (cleanText.includes('===HTML===')) {
          cleanText = cleanText.split('===HTML===').slice(1).join('===HTML===');
        }

        // Remove all lines containing === markers
        cleanText = cleanText.split('\n').filter(line => {
          return !line.includes('===');
        }).join('\n');

        // Sanitize to remove Cyrillic and other non-Croatian characters
        cleanText = this.sanitizeText(cleanText);

        if (cleanText.trim()) {
          assistantMessage.text += cleanText;
          this.$forceUpdate();
        }

        this.streamBuffer = '';
      }
    },

    async submitQuery() {
      if (!this.question || this.loading) return;

      const userQuestion = this.question.trim();

      // Clear any existing stream buffer
      this.streamBuffer = '';
      if (this.streamUpdateTimer) {
        clearTimeout(this.streamUpdateTimer);
        this.streamUpdateTimer = null;
      }

      // Save conversation history BEFORE adding current exchange
      const previousHistory = [...this.conversationHistory];

      // Add user message to conversation
      this.conversationHistory.push({
        isUser: true,
        text: userQuestion
      });

      // Clear input
      this.question = '';
      this.loading = true;

      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      // Create placeholder for assistant response
      const assistantMessage = {
        isUser: false,
        text: '',
        title: '',
        citations: []
      };
      this.conversationHistory.push(assistantMessage);

      // Temporary storage for citations (show at end)
      let tempCitations = [];

      try {
        await legalRag.queryLegalRagStream(
          userQuestion,
          previousHistory,
          this.previousCitations,
          (chunk) => {
            // Handle different chunk types
            if (chunk.type === 'citations') {
              // Store citations but don't display yet
              tempCitations = chunk.citations || [];
            } else if (chunk.type === 'title') {
              assistantMessage.title = chunk.title;
              this.$forceUpdate();
            } else if (chunk.type === 'content') {
              // Use batched updates for smooth streaming
              this.addToStreamBuffer(chunk.text, assistantMessage);
            } else if (chunk.type === 'complete') {
              // Flush any remaining buffered text
              this.flushStreamBuffer(assistantMessage);

              // Final update with complete data (sanitize for safety)
              assistantMessage.title = chunk.title ? this.sanitizeText(chunk.title) : assistantMessage.title;
              assistantMessage.text = chunk.answer ? this.sanitizeText(chunk.answer) : assistantMessage.text;
              // Now show citations at the end
              assistantMessage.citations = tempCitations;
              this.$forceUpdate();
            }
          },
          () => {
            // onComplete
            this.loading = false;

            // Update previous citations for next query
            this.previousCitations = assistantMessage.citations || [];

            // Save to localStorage
            this.saveConversationToStorage();

            // Final scroll
            this.$nextTick(() => {
              this.scrollToNewAnswer();
            });
          },
          (error) => {
            // onError
            console.error('Streaming query failed:', error);
            this.loading = false;

            // Replace assistant message with error
            assistantMessage.text = `<p class="error--text">${error.message || 'Greška pri pretraživanju'}</p><p class="caption">Pokušajte ponovno ili kontaktirajte podršku.</p>`;
            assistantMessage.title = 'Greška';
            this.$forceUpdate();

            // Scroll to show error
            this.$nextTick(() => {
              this.scrollToNewAnswer();
            });
          }
        );

      } catch (error) {
        console.error('Query failed:', error);
        this.loading = false;

        // Show error message
        let errorMessage = 'Greška pri pretraživanju';
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }

        assistantMessage.text = `<p class="error--text">${errorMessage}</p><p class="caption">Pokušajte ponovno ili kontaktirajte podršku.</p>`;
        assistantMessage.title = 'Greška';
        this.$forceUpdate();

        // Scroll to show error
        this.$nextTick(() => {
          this.scrollToNewAnswer();
        });
      }
    },

    openCitation(citation) {
      this.selectedCitation = citation;
      this.citationDialog = true;
    },

    copyCitation() {
      if (!this.selectedCitation) return;

      const text = `${this.selectedCitation.law_name}, ${this.selectedCitation.article_number}\n\n${this.selectedCitation.content}\n\nIzvor: ${this.selectedCitation.eli_url}`;

      // Copy to clipboard
      navigator.clipboard.writeText(text).then(() => {
        alert('Kopirano u međuspremnik!');
        this.citationDialog = false;
      }).catch(err => {
        console.error('Failed to copy:', err);
        alert('Greška pri kopiranju');
      });
    },

    clearConversation() {
      if (confirm('Želite li započeti novu konverzaciju? Trenutna povijest će biti izbrisana.')) {
        this.conversationHistory = [];
        this.previousCitations = [];

        // Set cleared flag in localStorage so it stays cleared on next open
        localStorage.setItem(CLEARED_FLAG_KEY, 'true');
        this.saveConversationToStorage();
      }
    },

    scrollToBottom() {
      const container = this.$refs.conversationContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    // Smart scroll: only auto-scroll if user is already near bottom
    smartScrollToBottom() {
      const container = this.$refs.conversationContainer;
      if (!container) return;

      // Check if user is near the bottom (within 100px)
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      // Only auto-scroll if user hasn't manually scrolled up
      if (isNearBottom) {
        container.scrollTop = container.scrollHeight;
      }
    },

    scrollToNewAnswer() {
      const container = this.$refs.conversationContainer;
      if (!container) return;

      // Get all message elements
      const messages = container.querySelectorAll('.message');
      if (messages.length === 0) return;

      // Get the last assistant message (the new answer)
      const lastMessage = messages[messages.length - 1];

      // Scroll so the top of the new message is visible with some padding
      const containerTop = container.getBoundingClientRect().top;
      const messageTop = lastMessage.getBoundingClientRect().top;
      const offset = messageTop - containerTop - 20; // 20px padding from top

      container.scrollBy({
        top: offset,
        behavior: 'smooth'
      });
    },

    loadConversationFromStorage() {
      // Always start with empty conversation
      this.conversationHistory = [];
      this.previousCitations = [];

      try {
        // Check if conversation was intentionally cleared
        const wasCleared = localStorage.getItem(CLEARED_FLAG_KEY);
        if (wasCleared === 'true') {
          // Remove the flag and stay with empty conversation
          localStorage.removeItem(CLEARED_FLAG_KEY);
          return;
        }

        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const data = JSON.parse(stored);
          if (data.version === STORAGE_VERSION) {
            this.conversationHistory = data.conversation || [];
            this.previousCitations = data.citations || [];
          }
        }
      } catch (error) {
        console.error('Failed to load conversation from storage:', error);
      }
    },

    saveConversationToStorage() {
      try {
        const data = {
          version: STORAGE_VERSION,
          conversation: this.conversationHistory,
          citations: this.previousCitations,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        console.error('Failed to save conversation to storage:', error);
      }
    }
  },

  beforeDestroy() {
    // Clean up stream buffer timer
    if (this.streamUpdateTimer) {
      clearTimeout(this.streamUpdateTimer);
      this.streamUpdateTimer = null;
    }
  }
};
</script>

<style scoped>
/* ============================================
   MODERN UI/UX STYLING - 2024/2025
   ============================================ */

/* ============================================
   CONVERSATION CONTAINER
   ============================================ */
.conversation-container {
  max-height: 100%;
  padding: 8px 0;
}

/* ============================================
   EMPTY STATE - Clean, Professional
   ============================================ */
.empty-state {
  text-align: center;
  padding: 3rem 2rem !important;
  max-width: 540px;
  margin: 0 auto;
}

.empty-state-title {
  font-size: 1.625rem;
  font-weight: 600;
  color: #2b2b41;
  line-height: 1.5;
  letter-spacing: -0.01em;
  margin: 0;
}

.example-questions {
  background: #ffffff;
  border-radius: 10px;
  padding: 3rem 2rem;
  margin-top: 2rem;
  box-shadow: 0 1px 3px rgba(43, 43, 65, 0.06);
  border: none;
}

.example-label {
  font-size: 1.375rem;
  font-weight: 600;
  color: #2b2b41;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1.5rem;
}

.example-chip {
  border: 1.5px solid rgba(43, 43, 65, 0.15) !important;
  color: #2b2b41 !important;
  font-size: 1.0625rem !important;
  padding: 1rem 1.25rem !important;
  height: auto !important;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background: #ffffff !important;
  font-weight: 500;
}

.example-chip:hover {
  background: #f8f9fa !important;
  border-color: rgba(43, 43, 65, 0.3) !important;
  box-shadow: 0 4px 12px rgba(43, 43, 65, 0.08);
  transform: translateY(-2px);
}

.example-chip:active {
  transform: translateY(-1px);
}

.example-chip >>> .v-icon {
  margin-right: 0.625rem !important;
}

/* ============================================
   MESSAGES - Clean, Modern Typography
   ============================================ */
.message {
  display: flex;
  margin-bottom: 2rem;
}

.message-content {
  position: relative;
  width: 100%;
}

.user-message {
  justify-content: flex-start;
}

.user-message .message-content {
  background: linear-gradient(135deg, #f8f9fd 0%, #fafbfe 100%);
  border-left: 3px solid #3325ae;
  border-radius: 0;
  padding: 1.75rem 1.5rem !important;
  max-width: 100%;
  font-size: 1.5rem;
  color: #1a1a2e;
  font-weight: 600;
  text-align: left;
  line-height: 1.5;
  letter-spacing: -0.01em;
  box-shadow: 0 1px 2px rgba(51, 37, 174, 0.04);
}

.assistant-message {
  justify-content: flex-start;
}

.assistant-message .message-content {
  background: #ffffff;
  border: none;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(43, 43, 65, 0.06), 0 2px 8px rgba(43, 43, 65, 0.04);
  padding: 2.25rem !important;
  color: #2b2b41;
  max-width: 100%;
  transition: box-shadow 0.2s ease;
  /* Optimize for smooth streaming updates */
  will-change: contents;
  transform: translateZ(0);
  backface-visibility: hidden;
  /* Enable hardware acceleration */
  perspective: 1000px;
  /* Smooth text rendering */
  text-rendering: optimizeSpeed;
}

.assistant-message .message-content:hover {
  box-shadow: 0 2px 4px rgba(43, 43, 65, 0.08), 0 4px 12px rgba(43, 43, 65, 0.06);
}

.answer-title {
  display: flex;
  align-items: center;
  font-size: 1.875rem;
  color: #3325ae;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.3;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e8e8f0;
}

/* ============================================
   LOADING MESSAGE - Smooth, Modern Animation
   ============================================ */
.loading-message {
  background: #ffffff !important;
  border: none !important;
  box-shadow: 0 1px 3px rgba(43, 43, 65, 0.06), 0 2px 8px rgba(43, 43, 65, 0.04) !important;
}

.loading-text {
  flex: 1;
}

.loading-title {
  font-size: 1.625rem;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 1.5rem;
  letter-spacing: -0.01em;
}

.loading-steps {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loading-step {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.25rem;
  color: #8b8b9a;
  transition: color 0.2s ease;
}

.loading-step.active {
  color: #2b2b41;
  font-weight: 600;
}

/* ============================================
   LEGAL RESPONSE CONTENT - Optimized Typography for Readability
   ============================================ */
.legal-response-content {
  font-size: 1.375rem;
  line-height: 1.85;
  color: #2b2b41;
  /* Optimize for smooth streaming */
  will-change: contents;
  contain: layout style;
  /* Smooth text rendering */
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.legal-response-content >>> h3 {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 2.5rem 0 1.5rem 0;
  color: #1a1a2e;
  letter-spacing: -0.02em;
  line-height: 1.3;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e8e8f0;
}

.legal-response-content >>> h3:first-child {
  margin-top: 0;
}

.legal-response-content >>> h4 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 2rem 0 1rem 0;
  color: #3325ae;
  letter-spacing: -0.01em;
  line-height: 1.4;
}

.legal-response-content >>> p {
  margin-bottom: 1.25rem;
  line-height: 1.85;
  font-size: 1.375rem;
}

.legal-response-content >>> strong,
.legal-response-content >>> b {
  font-weight: 700;
  color: #1a1a2e;
}

.legal-response-content >>> em {
  font-style: italic;
  color: #3325ae;
}

.legal-response-content >>> ul,
.legal-response-content >>> ol {
  margin: 1.5rem 0;
  padding-left: 2.25rem;
}

.legal-response-content >>> li {
  margin-bottom: 1rem;
  line-height: 1.8;
  font-size: 1.375rem;
}

.legal-response-content >>> li strong {
  color: #3325ae;
}

/* Special styling for important notes and sections */
.legal-response-content >>> p:has(strong:first-child) {
  background: #f8f9fd;
  border-left: 3px solid #3325ae;
  padding: 1.25rem 1.5rem;
  border-radius: 0;
  margin: 1.75rem 0;
}

/* Special styling for warnings/notes (Napomena:, Važno:, etc.) */
.legal-response-content >>> p > strong:first-child::after {
  content: '';
  display: inline-block;
  width: 0;
}

.legal-response-content >>> blockquote {
  border-left: 4px solid #0acab1;
  padding: 1.25rem 1.5rem;
  margin: 1.75rem 0;
  background: #f0fffe;
  border-radius: 0;
  font-style: italic;
  color: #2b2b41;
}

.legal-response-content >>> code {
  background: #f5f5f7;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 1.25rem;
  color: #3325ae;
}

.legal-response-content >>> .answer-section,
.legal-response-content >>> .info-section {
  margin-bottom: 2rem;
}

/* ============================================
   CITATIONS - Modern Card Design
   ============================================ */
.citations {
  padding-top: 2.5rem;
  margin-top: 2.5rem;
  border-top: 2px solid #e8e8f0;
}

.citations-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.citations-header-text {
  font-size: 1.125rem;
  font-weight: 600;
  color: #3325ae;
  letter-spacing: -0.01em;
}

.citations-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.citation-card {
  background: #f8f9fa;
  border: none;
  border-radius: 8px;
  padding: 1.75rem;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.citation-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #3325ae;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.citation-card:hover {
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(43, 43, 65, 0.08), 0 4px 16px rgba(43, 43, 65, 0.06);
  transform: translateY(-2px);
}

.citation-card:hover::before {
  opacity: 1;
}

.citation-card:active {
  transform: translateY(-1px);
}

.citation-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.citation-law-name {
  font-size: 1.375rem;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.4;
  letter-spacing: -0.01em;
}

.citation-article {
  font-size: 1.25rem;
  color: #606076;
  margin-bottom: 1.25rem;
  line-height: 1.5;
  font-weight: 500;
}

.citation-similarity {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.citation-similarity-text {
  font-size: 1.125rem;
  color: #7b7b8a;
  font-weight: 500;
}

/* ============================================
   INPUT SECTION - Clean, Modern
   ============================================ */
.input-section {
  background: #eeeff0;
  padding: 1.5rem;
  border-radius: 0;
  margin: 0 -1rem -1rem -1rem;
  border-top: 1px solid rgba(43, 43, 65, 0.06);
}

.input-section.compact {
  padding: 1rem 1.5rem;
}

.input-tip {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 1.125rem;
  color: #0a9b8a;
  font-weight: 500;
  padding: 0.75rem 1rem;
  background: rgba(10, 202, 177, 0.08);
  border-radius: 6px;
  border: none;
  margin-bottom: 1rem;
}

.legal-input {
  margin-bottom: 1.5rem;
}

.legal-input >>> .v-input__control {
  border-radius: 1rem !important;
}

.legal-input >>> fieldset {
  border: 2px solid #e0e0ea !important;
  border-radius: 1rem !important;
  transition: all 0.3s ease !important;
}

.legal-input >>> .v-text-field__slot {
  padding: 0.5rem 0 !important;
}

.legal-input:not(.v-input--is-disabled) >>> fieldset {
  background-color: #ffffff;
}

.legal-input.v-input--is-focused >>> fieldset {
  border-color: #342EAB !important;
  box-shadow: 0 0 0 3px rgba(52, 46, 171, 0.1) !important;
}

.legal-input >>> textarea {
  font-size: 1.125rem !important;
  line-height: 1.7 !important;
  color: #2b2b41 !important;
  padding: 1rem 1.25rem !important;
  margin-top: 0.25rem !important;
}

.legal-input >>> textarea::placeholder {
  color: #9b9ba8 !important;
  opacity: 1 !important;
  font-size: 1.0625rem !important;
}

.legal-input >>> label {
  font-size: 1.0625rem !important;
  color: #606076 !important;
  font-weight: 500 !important;
  background-color: #eeeff0 !important;
  padding: 0 0.5rem !important;
}

.legal-input.v-input--is-focused >>> label {
  color: #342EAB !important;
}

.legal-input.v-input--is-disabled >>> fieldset {
  border-color: #e8e8f0 !important;
  background-color: #f8f9f9 !important;
}

.legal-input.v-input--is-disabled >>> textarea {
  color: #9b9ba8 !important;
}

.submit-btn {
  background-color: #ffffff !important;
  border: 2px solid #e0e0ea !important;
  border-radius: 1rem !important;
  color: #000 !important;
  font-weight: 600 !important;
  font-size: 1.125rem !important;
  height: 5.5rem !important;
  padding: 1.5rem 2rem !important;
  text-transform: none !important;
  transition: all 0.35s ease !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

.submit-btn.compact {
  height: 4rem !important;
  padding: 1rem 1.5rem !important;
}

.submit-btn >>> .send-icon {
  color: #342EAB !important;
  transition: all 0.35s ease !important;
}

.submit-btn:hover:not(.v-btn--disabled) {
  background-color: #342EAB !important;
  border-color: #342EAB !important;
  color: #fff !important;
  box-shadow: none !important;
  transform: none !important;
}

.submit-btn:hover:not(.v-btn--disabled) >>> .send-icon {
  color: #fff !important;
}

.submit-btn:active:not(.v-btn--disabled) {
  transform: none;
}

.submit-btn.v-btn.v-btn--disabled {
  background-color: #efefef !important;
  color: #5a5a6e !important;
  opacity: 1 !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
  border-color: #e0e0ea !important;
}

.submit-btn.v-btn.v-btn--disabled::before {
  opacity: 0 !important;
}

.submit-btn.v-btn--disabled >>> .send-icon {
  color: #5a5a6e !important;
}

/* ============================================
   CITATION DIALOG - Professional, Clean
   ============================================ */
.citation-dialog >>> .v-card {
  border-radius: 0 !important;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(43, 43, 65, 0.12), 0 2px 8px rgba(43, 43, 65, 0.08) !important;
  border: 2px solid #3325ae;
}

.citation-header {
  background: linear-gradient(135deg, #3325ae 0%, #2817a0 100%);
  padding: 2.25rem;
  position: relative;
  overflow: hidden;
}

.citation-header-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 10px,
    rgba(255,255,255,0.03) 10px,
    rgba(255,255,255,0.03) 20px
  );
}

.citation-header-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.citation-header-text {
  flex: 1;
  color: white;
}

.citation-law-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  letter-spacing: -0.01em;
  line-height: 1.3;
}

.citation-article-title {
  font-size: 1.1875rem;
  font-weight: 500;
  opacity: 0.9;
  line-height: 1.4;
}

.citation-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  transition: transform 0.2s ease;
}

.citation-close-btn:hover {
  transform: scale(1.1);
}

.citation-content {
  background: #ffffff;
  padding: 2.5rem !important;
}

.citation-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.0625rem;
  font-weight: 700;
  color: #606076;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1.25rem;
}

.citation-body {
  font-size: 1.25rem !important;
  line-height: 1.85 !important;
  color: #2b2b41 !important;
  padding: 1.75rem 2rem;
  background: #f8f9fa;
  border-left: 3px solid #3325ae;
  border-radius: 0;
  font-weight: 400;
}

.citation-metadata {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1.75rem;
}

.metadata-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.125rem;
  color: #606076;
}

.metadata-label {
  font-weight: 600;
}

.citation-actions {
  background: #f8f9fa;
  border-top: 1px solid rgba(43, 43, 65, 0.06);
  padding: 1.5rem 2rem !important;
}

.citation-actions .v-btn {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1.0625rem;
}

.citation-actions .v-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.copy-citation-btn {
  color: #2b2b41 !important;
}

/* ============================================
   ACCESSIBILITY & FOCUS STATES
   ============================================ */
.v-btn:focus-visible {
  outline: 2px solid #3325ae;
  outline-offset: 2px;
}

.example-chip:focus-visible {
  outline: 2px solid #3325ae;
  outline-offset: 2px;
}

.citation-card:focus-visible {
  outline: 2px solid #3325ae;
  outline-offset: 2px;
}
</style>
